import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Collapse,
  Radio,
  useTheme,
  Tooltip
} from '@mui/material';
import { useController, Control } from 'react-hook-form';
import { MdBusiness, MdExpandMore } from 'react-icons/md';
import { FiLock } from 'react-icons/fi';
import { PackageResponse } from '@/apis/agency/businessPackages/types';
// import BusinessIcon from '@/assets/icons/BusinessIcon';
import { formatDollars } from '@/utils/helperFunctions';
import LocationIcon from '@/assets/icons/LocationIcon';
import { RiDeleteBin6Line, RiHardDrive3Line } from 'react-icons/ri';
import { useDeleteBusinessPlan } from '@/apis/agency/businessPackages/hook';
import { GoGlobe } from 'react-icons/go';
import { LuSettings } from 'react-icons/lu';

type PlanCardProps = {
  plan: PackageResponse;
  control?: Control<any>;
  name?: string;
  readOnly?: boolean;
  isDeleteAble?: boolean;
};

export default function PlanCard({ plan, control, name, readOnly = false, isDeleteAble = true }: PlanCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const { mutateAsync: deleteBusinessPlan } = useDeleteBusinessPlan();

  // Hook Form
  const { field } = useController({
    name: name || 'plan_id',
    control
  });

  const additionalCost = plan?.additional_per_min_charge;
  const minutesIncluded = plan?.available_minutes;
  const warmCallMinutesIncluded = plan?.available_warm_call_minutes;
  const additionalWarmCallCost = plan?.additional_warm_call_charge;
  const multilingual = plan?.price_details.multilingual_charge_per_min;
  const locationLimit = plan?.price_details?.location_limit?.limit;
  // const business = plan?.price_details?.business_cost;
  const integration = plan?.price_details?.integration_charge;
  const storage = plan?.price_details?.storage_charge_per_gb;

  if (!plan) {
    return;
  }

  const getBusinessNamesTitle = (businessNames?: string[]) => {
    if (!businessNames || businessNames.length === 0) {
      return 'No businesses';
    }

    return (
      <Box
        sx={{
          maxHeight: 250,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#212020', borderRadius: 2 },
          '&::-webkit-scrollbar-track': { backgroundColor: '#2c2b2b' }
        }}
      >
        <Typography sx={{ fontSize: '0.90rem', fontWeight: 'bold', mb: 0.5 }}>Business using this package:</Typography>
        {businessNames.map((planName, index) => (
          <Typography key={index} sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
            • {planName}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Card
      variant='outlined'
      sx={{
        height: '100%',
        borderRadius: 2,
        borderColor: '#ececec'
      }}
      onClick={() => {
        if (!readOnly) field.onChange(plan.id);
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, lg: 2 } }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1}>
          <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            {!readOnly && (
              <Radio
                disableRipple
                sx={{
                  '&.MuiButtonBase-root': { marginLeft: { xs: '-8px', lg: '-10px' } }
                }}
                checked={field.value === plan.id}
                onChange={() => field.onChange(plan.id)}
              />
            )}
            <Typography variant='subtitle1' fontWeight='bold' sx={{ fontSize: { xs: '0.875rem', lg: '1rem' } }}>
              {plan.name}
            </Typography>
            {!plan.public && <FiLock color='#777' size={18} />}
            <MdBusiness color='#777' size={16} />
            <Typography color='text.secondary' sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
              <Tooltip title={getBusinessNamesTitle(plan?.business_names)} placement='top' arrow>
                <span style={{ cursor: 'pointer' }}>{plan?.business_ids?.length ?? 0}</span>
              </Tooltip>
            </Typography>
          </Stack>
          <Stack direction='row' flexShrink={0}>
            {isDeleteAble && (
              <IconButton
                size='small'
                onClick={() => deleteBusinessPlan(String(plan?.id))}
                sx={{ padding: { xs: '4px', lg: '8px' } }}
              >
                <RiDeleteBin6Line />
              </IconButton>
            )}
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: '0.3s',
                padding: { xs: '4px', lg: '8px' }
              }}
            >
              <MdExpandMore size={18} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Price */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent='space-between'
          mt={1}
        >
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography variant='h6' color='primary' fontWeight='bold' sx={{ fontSize: { xs: '1rem', lg: '1.25rem' } }}>
              ${plan.base_price}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              borderLeft={{ xs: 'none', sm: '1px solid #ececec' }}
              marginRight={1}
              pl={{ xs: 0, sm: 1 }}
              sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
            >
              {minutesIncluded ?? 0} {minutesIncluded ? 'Minutes' : 'Minute'} included
            </Typography>
          </Box>
          {additionalCost && (
            <Box
              sx={{
                bgcolor: theme.palette.primary.light,
                color: theme.palette.primary.main,
                px: { xs: 1, lg: 1.5 },
                py: { xs: 0.4, lg: 0.5 },
                borderRadius: '16px',
                textWrap: 'nowrap',
                mt: { xs: 0, sm: 1 },
                display: 'inline-block',
                flexShrink: 0
              }}
            >
              <Typography variant='body2' fontWeight='medium' sx={{ fontSize: { xs: '11px', lg: '0.875rem' } }}>
                Additional: ${additionalCost}/Minute
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Expanded Details */}
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <CardContent sx={{ p: { xs: 1.5, lg: 2 }, pt: { xs: 0, lg: 0 } }}>
          <Stack spacing={1} borderTop='1px solid #ececec' paddingTop={{ xs: 1.5, lg: 2 }}>
            <Box
              sx={{
                bgcolor: field.value === plan.id ? theme.palette.primary.light : '#ECECEC',
                p: { xs: 1, lg: 1.2 },
                borderRadius: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 0.5, sm: 0 }
              }}
            >
              <Typography
                variant='body2'
                fontWeight='medium'
                color={field.value === plan.id ? 'primary' : 'secondary'}
                sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
              >
                Warm Call Minutes Included
              </Typography>
              <Typography
                variant='body2'
                fontWeight={600}
                color={field.value === plan.id ? 'primary' : 'secondary'}
                sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
              >
                {warmCallMinutesIncluded ?? 0}
              </Typography>
            </Box>
            {additionalWarmCallCost != null && (
              <Box
                sx={{
                  bgcolor: field.value === plan.id ? theme.palette.primary.light : '#ECECEC',
                  p: { xs: 1, lg: 1.2 },
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 0.5, sm: 0 }
                }}
              >
                <Typography
                  variant='body2'
                  fontWeight='medium'
                  color={field.value === plan.id ? 'primary' : 'secondary'}
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  Warm Call Additional
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color={field.value === plan.id ? 'primary' : 'secondary'}
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  ${additionalWarmCallCost}/Minute
                </Typography>
              </Box>
            )}
            {multilingual && multilingual.enabled && (
              <Box
                sx={{
                  bgcolor: field.value === plan.id ? theme.palette.primary.light : '#ECECEC',
                  p: { xs: 1, lg: 1.2 },
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 0.5, sm: 0 }
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1.2}>
                  <GoGlobe style={{ color: field.value === plan.id ? theme.palette.primary.main : '#777777' }} />
                  <Typography
                    variant='body2'
                    fontWeight='medium'
                    color={field.value === plan.id ? 'primary' : 'secondary'}
                    sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                  >
                    Multilingual Charges
                  </Typography>
                </Stack>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color={field.value === plan.id ? 'primary' : 'secondary'}
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  {formatDollars(multilingual?.unit_amount)}/Minute
                </Typography>
              </Box>
            )}
            {Number(locationLimit) > 0 && (
              <Box
                sx={{
                  bgcolor: field.value === plan.id ? theme.palette.primary.light : '#ECECEC',
                  p: { xs: 1, lg: 1.2 },
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 0.5, sm: 0 }
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  color={field.value === plan.id ? theme.palette.primary.main : '#777777'}
                >
                  <LocationIcon size={20} />
                  <Typography
                    variant='body2'
                    fontWeight='medium'
                    color={field.value === plan.id ? 'primary' : 'secondary'}
                    sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                  >
                    Location Limit
                  </Typography>
                </Stack>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color={field.value === plan.id ? 'primary' : 'secondary'}
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  {locationLimit}
                </Typography>
              </Box>
            )}
            {integration && integration.enabled && (
              <Box
                sx={{
                  bgcolor: field.value === plan.id ? theme.palette.primary.light : '#ECECEC',
                  p: { xs: 1, lg: 1.2 },
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 0.5, sm: 0 }
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1.2}>
                  <LuSettings style={{ color: field.value === plan.id ? theme.palette.primary.main : '#777777' }} />
                  <Typography
                    variant='body2'
                    fontWeight='medium'
                    color={field.value === plan.id ? 'primary' : 'secondary'}
                    sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                  >
                    Integration Charges
                  </Typography>
                </Stack>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color={field.value === plan.id ? 'primary' : 'secondary'}
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  {integration.enabled ? 'ON' : 'OFF'}
                </Typography>
              </Box>
            )}
            {storage && storage.enabled && (
              <Box
                sx={{
                  bgcolor: field.value === plan.id ? theme.palette.primary.light : '#ECECEC',
                  p: { xs: 1, lg: 1.2 },
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 0.5, sm: 0 }
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1.2}>
                  <RiHardDrive3Line
                    style={{ color: field.value === plan.id ? theme.palette.primary.main : '#777777' }}
                  />
                  <Typography
                    variant='body2'
                    fontWeight='medium'
                    color={field.value === plan.id ? 'primary' : 'secondary'}
                    sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                  >
                    Storage
                  </Typography>
                </Stack>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color={field.value === plan.id ? 'primary' : 'secondary'}
                  sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                >
                  {formatDollars(storage?.unit_amount)}/GB
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
}
